import { useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import {
  AttachMoney as DollarIcon,
  EmojiEvents as AwardIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { Text } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Shift } from "@src/interface";
import { formatPhone } from "src/utils/strings";
import { formatDateTime } from "@clipboard-health/date-time";

export function ShiftMoreDetailsAccordion({ shift }: { shift: Shift }) {
  const [expandedMoreDetails, setExpandedDetails] = useState(true);
  return (
    <Accordion
      expanded={expandedMoreDetails}
      onChange={() => setExpandedDetails(!expandedMoreDetails)}
      sx={{ bgcolor: "#ffffff" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f9ff" }}>
        <Text variant="subtitle1" bold>
          More Information
        </Text>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {/* Shift status information */}
          <Box>
            <Text variant="body2" bold>
              Shift Status
            </Text>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {shift.clockInOut?.end ? (
                <Chip
                  size="small"
                  label="Completed"
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
                />
              ) : // eslint-disable-next-line no-nested-ternary
              isDefined(shift.agentId) ? (
                <Chip size="small" label="Assigned" sx={{ bgcolor: "#ffb74d", color: "#e65100" }} />
              ) : (
                <Chip size="small" label="Open" sx={{ bgcolor: "#ffebee", color: "#d32f2f" }} />
              )}
              {shift.verified && (
                <Chip size="small" label="Verified" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }} />
              )}
            </Stack>
          </Box>

          {/* Instant Pay Information */}
          {shift.instantPayDetails && (
            <Box>
              <Text variant="body2" bold>
                Instant Pay
              </Text>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Text variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                  <DollarIcon sx={{ fontSize: 16, color: "#2e7d32", mr: 0.5 }} />
                  Instant Pay Enabled
                </Text>
                {shift.instantPayDetails.paidAmount && (
                  <Text variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <ReceiptIcon sx={{ fontSize: 16, color: "#2e7d32", mr: 0.5 }} />$
                    {shift.instantPayDetails.paidAmount} paid instantly
                  </Text>
                )}
              </Stack>
            </Box>
          )}

          {shift.priorityTill && (
            <Box>
              <Text variant="body2" bold>
                Priority Access
              </Text>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Text variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                  <AwardIcon sx={{ fontSize: 16, color: "#9c27b0", mr: 0.5 }} />
                  Priority until {formatDateTime(new Date(shift.priorityTill))}
                </Text>
              </Stack>
            </Box>
          )}

          {/* Still Bookable Information */}
          {shift.extendBookabilityTo && (
            <Box>
              <Text variant="body2" bold>
                Extended Booking Window
              </Text>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Text variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                  <ScheduleIcon sx={{ fontSize: 16, color: "#1976d2", mr: 0.5 }} />
                  Bookable until {formatDateTime(new Date(shift.extendBookabilityTo))}
                </Text>
              </Stack>
            </Box>
          )}

          {/* Shift Specifications */}
          {isDefined(shift.shiftSpecifications) && shift.shiftSpecifications?.length > 0 && (
            <Box>
              <Text variant="body2" bold>
                Shift Specifications
              </Text>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {Object.entries(shift.shiftSpecifications[0] || {}).map(([key, value]) => (
                  <Text key={key} variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    <AssignmentIcon sx={{ fontSize: 16, color: "#0288d1", mr: 0.5 }} />
                    {key}: {String(value)}
                  </Text>
                ))}
              </Stack>
            </Box>
          )}

          {/* Interested Workers */}
          {shift.interested && shift.interested.length > 0 && (
            <Box>
              <Text variant="body2" bold>
                Interested Workers
              </Text>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {shift.interested
                  .filter((agent) => isDefined(agent))
                  .map((agent) => (
                    <Tooltip key={agent._id} title={agent.phone ? formatPhone(agent.phone) : ""}>
                      <Chip
                        size="small"
                        label={agent.name}
                        sx={{
                          bgcolor: "#e8eaf6",
                          color: "#3949ab",
                          mb: 0.5,
                        }}
                      />
                    </Tooltip>
                  ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
